import React, { useState, useRef } from "react";
import { Button, Spin, Image as AntImage } from "antd";
import {
  CloseOutlined,
  FilePdfOutlined,
  UploadOutlined,
  EyeOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import Text from "../../context/language-context";
import { eDocType, eFORMSTATUS, eResultCode } from "../../utils/enum";
import { validateFile } from "../../utils/input.validator";
import { UploadDocs } from "../../utils/api.constant";
import AuthUtil from "../../utils/auth";
import useToast from "../../hooks/useToast";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import styles from "./document-upload.module.css";
import { IoMdRefresh } from "react-icons/io";

/**
 * Props for the DocumentUploadTableField component
 */
export interface DocumentUploadTableFieldProps {
  /** Form ID for upload API */
  formId: string;
  /** User ID for upload API */
  userId: string | number;
  /** Document label/field name for the upload */
  labelName: string;
  /** Form stage for upload API (default: STAGE2INPROGRESS) */
  currentStage?: number;
  /** Document type for upload API (default: eDocType.OTHER) */
  docType?: eDocType;
  /** Accept attribute for file input (default: '.jpg,.jpeg,.png,.pdf') */
  acceptTypes?: string;
  /** Already uploaded document URL/path */
  existingDocumentUrl?: string;
  /** Callback when upload completes successfully - receives the document URL */
  onUploadComplete?: (docUrl: string) => void;
  /** Callback when upload fails - receives error message */
  onUploadError?: (error: string) => void;
  /** Disable the field */
  disabled?: boolean;
  /** Show compact view for tables */
  compact?: boolean;
}

/**
 * A reusable document upload field component designed for use within table cells.
 * Handles file validation, uploading to backend API, and displaying preview/replace buttons.
 *
 * @example
 * ```tsx
 * <DocumentUploadTableField
 *   formId={formId || ""}
 *   userId={user?.id || ""}
 *   labelName={`SCHOOL_PT2_Q2_${index}`}
 *   existingDocumentUrl={record.documentUrl}
 *   onUploadComplete={(docUrl) => handleDocumentUpload(index, docUrl)}
 *   compact
 * />
 * ```
 */
const DocumentUploadTableField: React.FC<DocumentUploadTableFieldProps> = ({
  formId,
  userId,
  labelName,
  currentStage = eFORMSTATUS.STAGE2INPROGRESS,
  docType = eDocType.OTHER,
  acceptTypes = ".jpg,.jpeg,.png,.pdf",
  existingDocumentUrl,
  onUploadComplete,
  onUploadError,
  disabled = false,
  compact = true,
}) => {
  const { onShowToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(
    existingDocumentUrl || null,
  );
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // i18n text
  const PREVIEW = Text({ tid: "PREVIEW", def: "Preview" });
  const REPLACE_FILE = Text({ tid: "REPLACE_FILE", def: "Replace" });
  const CHOOSE_FILE = Text({ tid: "CHOOSE_FILE", def: "Upload" });

  /**
   * Check if a file is already uploaded (either new or existing)
   */
  const hasUploadedFile = Boolean(documentUrl || existingDocumentUrl);

  /**
   * Upload document to the backend
   */
  const uploadDocument = async (file: File) => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", docType.toString());
      formData.append("formId", formId);
      formData.append("userId", userId.toString());
      formData.append("labelName", labelName);
      formData.append("currentStage", currentStage.toString());

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        },
      );

      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        const docUrl = data.docUrl.toString();
        setDocumentUrl(docUrl);
        onUploadComplete?.(docUrl);
      } else {
        const errorMessage = "Failed to upload document";
        console.error(errorMessage, response.statusText);
        onUploadError?.(errorMessage);
      }
    } catch (error) {
      const errorMessage = "An error occurred while uploading";
      console.error(error);
      onUploadError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const validation = validateFile(file);

    if (!validation.valid) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: validation.error,
      });
      e.target.value = "";
      return;
    }

    uploadDocument(file);
  };

  /**
   * Trigger file input click
   */
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Check if the file path is an image based on extension
   */
  const isImageFile = (path: string): boolean => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    const lowerPath = path.toLowerCase();
    return imageExtensions.some((ext) => lowerPath.endsWith(ext));
  };

  /**
   * Open document in new tab for preview
   */
  const handlePreview = () => {
    const docPath = documentUrl || existingDocumentUrl;
    if (docPath) {
      const fullUrl = `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docPath}`;
      window.open(fullUrl, "_blank");
    }
  };

  // Get full document URL for preview
  const docPath = documentUrl || existingDocumentUrl;
  const fullUrl = docPath
    ? `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${docPath}`
    : "";

  return (
    <div
      style={{
        display: "flex",
        gap: "15px",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
      id='document-upload-table-field'
    >
      <Spin
        spinning={isUploading}
        size='small'
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type='file'
          accept={acceptTypes}
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={disabled || isUploading}
        />

        {/* Compact view for tables */}
        {compact ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {hasUploadedFile ? (
              <>
                {/* Show thumbnail for images, icon for PDFs */}
                {isImageFile(docPath || "") ? (
                  <AntImage
                    src={fullUrl}
                    alt={labelName}
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "cover",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                    preview={{
                      mask: <EyeOutlined />,
                    }}
                  />
                ) : (
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={handlePreview}
                    size='small'
                    type='text'
                    title={PREVIEW}
                  />
                )}
                {/* Replace button - only show if not disabled */}
                {!disabled && (
                  <Button
                    icon={<RetweetOutlined />}
                    onClick={handleButtonClick}
                    loading={isUploading}
                    size='small'
                    type='text'
                    title={REPLACE_FILE}
                  />
                )}
              </>
            ) : (
              // No document - show upload button
              <Button
                icon={<UploadOutlined />}
                onClick={handleButtonClick}
                loading={isUploading}
                disabled={disabled}
                size='small'
                type='primary'
              >
                {CHOOSE_FILE}
              </Button>
            )}
          </div>
        ) : (
          // Full view (non-compact) - keep original button layout
          <>
            {hasUploadedFile ? (
              <>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  size='middle'
                  type='link'
                >
                  {PREVIEW}
                </Button>
                {!disabled && (
                  <Button
                    icon={<UploadOutlined />}
                    onClick={handleButtonClick}
                    loading={isUploading}
                    size='middle'
                  >
                    {REPLACE_FILE}
                  </Button>
                )}
              </>
            ) : (
              <Button
                icon={<UploadOutlined />}
                onClick={handleButtonClick}
                loading={isUploading}
                disabled={disabled}
                size='middle'
              >
                {CHOOSE_FILE}
              </Button>
            )}
          </>
        )}
      </Spin>
    </div>
  );
};

export default DocumentUploadTableField;
